/**
 * Helpers for cascading category-attribute filter state with nested definitions.
 */

const parentValueKey = (parentAttributeId, parentValueId) =>
  `${Number(parentAttributeId)}:${Number(parentValueId)}`;

/**
 * Collects categoryAttributeId values from a nested attribute definition subtree.
 */
export function collectNestedAttributeIds(attributeDefinition) {
  const ids = [];
  const walk = (attr) => {
    if (!attr || attr.id == null) return;
    ids.push(Number(attr.id));
    const values = attr.categoryAttributesValueDTO || [];
    for (const val of values) {
      if (val?.categoryAttributesDTO) {
        walk(val.categoryAttributesDTO);
      }
    }
  };
  walk(attributeDefinition);
  return ids;
}

/**
 * Collects attribute ids and predefined option ids that belong to one nested branch.
 */
export function collectBranchFilterScope(attributeDefinition) {
  const attributeIds = new Set();
  const optionIdsByAttributeId = new Map();

  const walk = (attr) => {
    if (!attr || attr.id == null) return;
    const attributeId = Number(attr.id);
    attributeIds.add(attributeId);

    const optionIds = optionIdsByAttributeId.get(attributeId) ?? new Set();
    for (const val of attr.categoryAttributesValueDTO || []) {
      if (val?.id != null) {
        optionIds.add(Number(val.id));
      }
      if (val?.categoryAttributesDTO) {
        walk(val.categoryAttributesDTO);
      }
    }
    optionIdsByAttributeId.set(attributeId, optionIds);
  };

  walk(attributeDefinition);

  return { attributeIds, optionIdsByAttributeId };
}

/**
 * Map `${parentAttributeId}:${parentValueId}` -> descendant categoryAttributeIds.
 */
export function buildDescendantIdsByParentValueMap(definitions) {
  const map = new Map();
  if (!Array.isArray(definitions)) {
    return map;
  }

  const walkAttribute = (attr) => {
    if (!attr) return;
    const values = attr.categoryAttributesValueDTO || [];
    for (const val of values) {
      if (val?.categoryAttributesDTO) {
        map.set(
          parentValueKey(attr.id, val.id),
          collectNestedAttributeIds(val.categoryAttributesDTO)
        );
        walkAttribute(val.categoryAttributesDTO);
      }
    }
  };

  for (const attr of definitions) {
    walkAttribute(attr);
  }
  return map;
}

/**
 * Map `${parentAttributeId}:${parentValueId}` -> branch scope for scoped cleanup.
 */
export function buildBranchScopeByParentValueMap(definitions) {
  const map = new Map();
  if (!Array.isArray(definitions)) {
    return map;
  }

  const walkAttribute = (attr) => {
    if (!attr) return;
    for (const val of attr.categoryAttributesValueDTO || []) {
      if (val?.categoryAttributesDTO) {
        map.set(
          parentValueKey(attr.id, val.id),
          collectBranchFilterScope(val.categoryAttributesDTO)
        );
        walkAttribute(val.categoryAttributesDTO);
      }
    }
  };

  for (const attr of definitions) {
    walkAttribute(attr);
  }
  return map;
}

const hasActiveUserAttributeValues = (userAttributeValues) =>
  Boolean(
    userAttributeValues &&
      Object.values(userAttributeValues).some(
        (val) => val !== null && val !== "" && val !== undefined
      )
  );

/**
 * Removes filter entries for descendant attribute ids under a parent option value.
 */
export function removeDescendantFilterEntries(
  filterEntries,
  parentAttributeId,
  parentValueId,
  descendantIdsByParentValue
) {
  if (!Array.isArray(filterEntries)) {
    return [];
  }
  const descendantIds = descendantIdsByParentValue.get(
    parentValueKey(parentAttributeId, parentValueId)
  );
  if (!descendantIds?.length) {
    return filterEntries;
  }
  const removeSet = new Set(descendantIds.map((id) => Number(id)));
  return filterEntries.filter(
    (entry) => !removeSet.has(Number(entry.categoryAttributeId))
  );
}

/**
 * Prunes only values that belong to the unchecked parent branch.
 * Sibling branches can share the same categoryAttributeId in flat filter state.
 */
export function pruneDescendantFilterEntries(
  filterEntries,
  parentAttributeId,
  parentValueId,
  branchScopeByParentValue
) {
  if (!Array.isArray(filterEntries)) {
    return [];
  }

  const branchScope = branchScopeByParentValue.get(
    parentValueKey(parentAttributeId, parentValueId)
  );
  if (!branchScope?.attributeIds?.size) {
    return filterEntries;
  }

  const { attributeIds, optionIdsByAttributeId } = branchScope;

  return filterEntries
    .map((entry) => {
      const attributeId = Number(entry.categoryAttributeId);
      if (!attributeIds.has(attributeId)) {
        return entry;
      }

      const branchOptionIds = optionIdsByAttributeId.get(attributeId);
      const hasPredefinedValues =
        Array.isArray(entry.itemAttributeValues) &&
        entry.itemAttributeValues.length > 0;

      if (hasPredefinedValues && branchOptionIds?.size) {
        const nextValues = entry.itemAttributeValues.filter(
          (valueId) => !branchOptionIds.has(Number(valueId))
        );
        if (nextValues.length === 0 && !hasActiveUserAttributeValues(entry.userAttributeValues)) {
          return null;
        }
        return { ...entry, itemAttributeValues: nextValues };
      }

      if (hasActiveUserAttributeValues(entry.userAttributeValues)) {
        return null;
      }

      if (hasPredefinedValues) {
        return entry;
      }

      return null;
    })
    .filter(Boolean);
}
